import { useQuery } from '@tanstack/react-query';
import type { LocaleOption } from '@course-authoring/shared-types';
import { normalizeAuthoringLocale } from '../utils/contentLocale';
import { fetchWithAuthRetry, handleApiResponse } from './apiUtils';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

export const ENGLISH_US_LOCALE_OPTION: LocaleOption = {
  locale: 'en',
  label: 'English (US)',
};

export const contentLocaleKeys = {
  all: ['content-locales'] as const,
};

function isLocaleOption(value: unknown): value is LocaleOption {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Record<string, unknown>;
  return typeof candidate.locale === 'string' && typeof candidate.label === 'string';
}

export async function fetchContentLocales(): Promise<LocaleOption[]> {
  const response = await fetchWithAuthRetry(`${API_BASE_URL}/content-locales`);
  const payload = await handleApiResponse<unknown>(response, 'Failed to load supported languages');
  if (!Array.isArray(payload)) {
    throw new Error('Invalid supported-language response');
  }

  const seen = new Set<string>([ENGLISH_US_LOCALE_OPTION.locale]);
  const options: LocaleOption[] = [ENGLISH_US_LOCALE_OPTION];
  for (const value of payload) {
    if (!isLocaleOption(value)) continue;
    const locale = normalizeAuthoringLocale(value.locale);
    const label = value.label.trim();
    if (!locale || !label || seen.has(locale)) continue;
    seen.add(locale);
    options.push({ locale, label });
  }
  return options;
}

export function useContentLocales(enabled = true) {
  return useQuery({
    queryKey: contentLocaleKeys.all,
    queryFn: fetchContentLocales,
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}
